import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CorporateAccountOpenProfileDetailsPages, useCorporateAccountOpenProfileDetailsPageTypeStore } from 'views/pages/accountOpenProfileDetails/corporateProfileDetails/hooks';
import { useEffect } from 'react';
import { useCorporateAccountEstablishStore } from '../hooks';
import { ErrorWord, getMaxLengthErrorMessage, TemplateProps, } from '../common';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { AccountType } from 'views/templates/module/bankDetailsInput/hooks';


export type BankInfoType = {
  bankName: string;
  branchName: string;
  accountType : AccountType | null;
  accountNumber : string;
  accountHolderName : string;
};

export const useBankInfo = ({ isUpdate }: TemplateProps) => {
  const { param, add } = useCorporateAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();
  const { messageClose } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validation = Yup.object().shape({    
    bankName: Yup.string().optional().max(255, getMaxLengthErrorMessage(255)),
    branchName: Yup.string().when('bankName', {
                  is: (value: string) => !!value && value.trim().length > 0,
                  then: () => Yup.string().required(ErrorWord.INPUT),
                  otherwise: (schema) => schema.strip(), 
                }).max(255, getMaxLengthErrorMessage(255)),
    accountType: Yup.string().when('bankName', {
                  is: (value: string) => !!value && value.trim().length > 0,
                  then: () =>  Yup.number().not([AccountType.None], ErrorWord.SELECT),
                  otherwise: (schema) => schema.strip(), 
                }),
    accountNumber: Yup.string().when('bankName', {
                  is: (value: string) => !!value && value.trim().length > 0,
                  then: () =>  Yup.string().required(ErrorWord.INPUT).length(7, ErrorWord.BANKNUMBER).matches(/^[0-9]*$/gi, ErrorWord.BANKNUMBER),
                  otherwise: (schema) => schema.strip(), 
                }),
    accountHolderName: Yup.string().when('bankName', {
                  is: (value: string) => !!value && value.trim().length > 0,
                  then: () =>  Yup.string().required(ErrorWord.INPUT).max(255, getMaxLengthErrorMessage(255)),
                  otherwise: (schema) => schema.strip(), 
                }),
  });

  const initialValues: BankInfoType = {
    bankName: param.bankName ?? '',
    branchName: param.branchName ?? '',
    accountType : param.accountType,
    accountNumber : param.accountNumber ?? '',
    accountHolderName : param.accountHolderName ?? '',
  };

  const formik = useFormik<BankInfoType>({
    enableReinitialize: isUpdate,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.entryConfirm);
    },
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      // バリデーションかける時に必ずエラートーストを閉じる
      messageClose();
    },
  });


  const bankDetailsInfoProps = {
    selectBankProps : {
      selected : formik.values.bankName,
      onSelectedBank: (value: string) => {formik.setFieldValue('bankName', value)},
      error: formik.errors.bankName
    },
    selectBranchProps : {
      selected : formik.values.branchName,
      onSelectedBranch: (value: string) => {formik.setFieldValue('branchName', value)},
      error: formik.errors.branchName
    },
    accountTypeProps : {
      selected: formik.values.accountType,
      onChange: (value: string) => formik.setFieldValue('accountType', value),
      error: formik.errors.accountType,
    },
    accountNumberProps : {
      value: formik.values.accountNumber,
      onChange: (value: string) => formik.setFieldValue('accountNumber', value),
      error: formik.errors.accountNumber,
      },
    accountHolderNameProps : {
      value: formik.values.accountHolderName,
      onChange: (value: string) => formik.setFieldValue('accountHolderName', value),
      error: formik.errors.accountHolderName,
    },
  }

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    bankDetailsInfoProps,
  };
};
